/**
 * Wires the relay client to the transcript reducer.
 *
 * The reducer is the source of truth; this hook only owns the socket's
 * lifetime. `lastSegmentSeq` is read through a ref at connect time so a
 * reconnect resumes from what the browser actually holds, not from whatever was
 * true when the hook first ran.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Envelope, EventType, Payloads, Role } from '@lt/protocol';
import { RelayClient, type ConnectionState } from '../lib/relay-client.js';
import { relayUrls } from '../lib/api.js';
import { initialState, reduce, type TranscriptState } from '../lib/transcript-state.js';

export interface UseSession {
  state: TranscriptState;
  connection: ConnectionState;
  send: <K extends EventType>(type: K, payload: Payloads[K]) => void;
  recordLatency: (kind: 'touch' | 'scroll' | 'catchup', ms: number) => void;
  /** Subscribes to raw events — used for follow events, which are not reduced. */
  onEvent: (handler: (event: Envelope) => void) => () => void;
}

export function useSession(options: {
  sessionId: string;
  token: string;
  role: Exclude<Role, 'relay'>;
}): UseSession {
  const [state, setState] = useState<TranscriptState>(initialState);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const clientRef = useRef<RelayClient | null>(null);
  const lastSeqRef = useRef<number | null>(null);
  const listenersRef = useRef(new Set<(event: Envelope) => void>());

  useEffect(() => {
    const { httpBase, wsUrl } = relayUrls();
    const client = new RelayClient({
      wsUrl,
      httpBase,
      token: options.token,
      role: options.role,
      sessionId: options.sessionId,
      lastSegmentSeq: () => lastSeqRef.current,
      onState: setConnection,
      onEvent: (event) => {
        setState((current) => {
          const next = reduce(current, event);
          // Keep the ref in step synchronously: a reconnect may be scheduled
          // before React has committed the new state.
          if (next.lastSegmentSeq !== null) lastSeqRef.current = next.lastSegmentSeq;
          return next;
        });
        for (const listener of listenersRef.current) listener(event);
      },
    });
    clientRef.current = client;
    client.connect();

    return () => {
      client.close();
      clientRef.current = null;
    };
  }, [options.sessionId, options.token, options.role]);

  const send = useCallback(<K extends EventType>(type: K, payload: Payloads[K]) => {
    clientRef.current?.send(type, payload);
  }, []);

  const recordLatency = useCallback((kind: 'touch' | 'scroll' | 'catchup', ms: number) => {
    clientRef.current?.recordLatency(kind, ms);
  }, []);

  const onEvent = useCallback((handler: (event: Envelope) => void) => {
    listenersRef.current.add(handler);
    return () => void listenersRef.current.delete(handler);
  }, []);

  return { state, connection, send, recordLatency, onEvent };
}
